/**
 * propinaAllocation.ts — Reparto de propina/servicio por método de pago y agregados.
 *
 * Qué hace:
 *   Calcula cuánto de la propina corresponde a cada método (incl. pago mixto proporcional)
 *   y agrega totales para historial / reparto por empleado.
 *
 * Qué invoca: tipos Venta / PaymentBreakdown (solo lectura).
 * Exporta: helpers puros sin UI ni storage.
 *
 * NO debe: llamar APIs, leer localStorage ni importar componentes React.
 */

import type { PaymentBreakdown, Venta } from "../types/caja.types";
import { txRead } from "@/lib/persistence/transactionalStore";
import {
  allocateVentaAmountBySlot,
  normalizePaymentSlotId,
  slotToLegacyBucket,
} from "./paymentMethodAggregation";

export type MontosPorMetodoPago = {
  efectivo: number;
  nequi: number;
  daviplata: number;
  tarjeta: number;
};

export const METODOS_PROPINA = [
  "efectivo",
  "nequi",
  "daviplata",
  "tarjeta",
] as const;

export type MetodoPropinaKey = (typeof METODOS_PROPINA)[number];

const EMPTY: MontosPorMetodoPago = {
  efectivo: 0,
  nequi: 0,
  daviplata: 0,
  tarjeta: 0,
};

const toPropinaKey = (method: string): MetodoPropinaKey | null => {
  const bucket = slotToLegacyBucket(normalizePaymentSlotId(method));
  if (bucket === "otros") return null;
  return bucket as MetodoPropinaKey;
};

/** Monto de servicio/propina persistido en la venta (0 si venta antigua sin campo). */
export const getVentaServicioAmount = (v: Venta): number => {
  const n = v.servicioAmount;
  if (typeof n === "number" && n > 0) return Math.round(n);
  return 0;
};

export const ventaTienePropina = (v: Venta): boolean =>
  getVentaServicioAmount(v) > 0;

/** Subtotal de productos (sin servicio/propina). */
export const getVentaSubtotal = (v: Venta): number => {
  if (typeof v.subtotal === "number" && v.subtotal >= 0) {
    return Math.round(v.subtotal);
  }
  return Math.max(0, Math.round(v.total - getVentaServicioAmount(v)));
};

/** Reparte un monto (subtotal o propina) entre métodos según el cobro de la venta. */
export const allocateVentaMontoPorMetodo = (
  v: Venta,
  amount: number,
): MontosPorMetodoPago =>
  buildServicioPorMetodo({
    total: v.total,
    servicioAmount: Math.max(0, Math.round(amount)),
    metodoPago: v.metodoPago,
    breakdown: v.detalleMetodoPago?.breakdown,
  });

/** Propina asignada a un método concreto. */
export const getPropinaPorMetodo = (
  v: Venta,
  metodo: MetodoPropinaKey,
): number => {
  const stored = v.servicioPorMetodo?.[metodo];
  if (typeof stored === "number" && stored >= 0) return stored;
  const amt = getVentaServicioAmount(v);
  if (amt <= 0) return 0;
  const built = buildServicioPorMetodo({
    total: v.total,
    servicioAmount: amt,
    metodoPago: v.metodoPago,
    breakdown: v.detalleMetodoPago?.breakdown,
  });
  return built[metodo] ?? 0;
};

export interface BuildServicioPorMetodoInput {
  total: number;
  servicioAmount: number;
  metodoPago: string;
  breakdown?: PaymentBreakdown[];
}

/**
 * Reparte la propina entre métodos: 100 % al método único, o proporcional al breakdown en mixto.
 */
export const buildServicioPorMetodo = (
  input: BuildServicioPorMetodoInput,
): MontosPorMetodoPago => {
  const svc = Math.max(0, Math.round(input.servicioAmount));
  if (svc <= 0) return { ...EMPTY };

  const breakdown = input.breakdown?.filter((b) => b.amount > 0) ?? [];
  const isMixto =
    input.metodoPago === "mixto" ||
    (breakdown.length > 1 && new Set(breakdown.map((b) => b.method)).size > 1);

  if (!isMixto && breakdown.length <= 1) {
    const method = breakdown[0]?.method ?? input.metodoPago;
    const out = { ...EMPTY };
    const key = toPropinaKey(method);
    if (key) out[key] = svc;
    return out;
  }

  const payTotal = breakdown.reduce((s, b) => s + b.amount, 0) || input.total;
  if (payTotal <= 0) return { ...EMPTY };

  const out = { ...EMPTY };
  let allocated = 0;
  breakdown.forEach((line, i) => {
    const key = toPropinaKey(line.method);
    if (!key) return;
    const isLast = i === breakdown.length - 1;
    const share = isLast
      ? svc - allocated
      : Math.round((svc * line.amount) / payTotal);
    out[key] += Math.max(0, share);
    allocated += isLast ? share : Math.max(0, share);
  });
  return out;
};

/** Suma propinas por método sobre un conjunto de ventas activas. */
export const sumPropinaPorMetodo = (ventas: Venta[]): MontosPorMetodoPago => {
  const out = { ...EMPTY };
  ventas.forEach((v) => {
    if (v.estado === "anulada") return;
    const porMetodo =
      v.servicioPorMetodo ??
      buildServicioPorMetodo({
        total: v.total,
        servicioAmount: getVentaServicioAmount(v),
        metodoPago: v.metodoPago,
        breakdown: v.detalleMetodoPago?.breakdown,
      });
    METODOS_PROPINA.forEach((m) => {
      out[m] += porMetodo[m] ?? 0;
    });
  });
  return out;
};

export const totalPropinaMontos = (montos: MontosPorMetodoPago): number =>
  montos.efectivo + montos.nequi + montos.daviplata + montos.tarjeta;

export interface PropinaPorEmpleadoRow {
  usuarioId: string;
  userName: string;
  porMetodo: MontosPorMetodoPago;
  total: number;
  ventasConPropina: number;
}

/** Agrupa propinas por quien registró la venta (vendedor/cajero). */
export const groupPropinaPorEmpleado = (
  ventas: Venta[],
): PropinaPorEmpleadoRow[] => {
  const map = new Map<string, PropinaPorEmpleadoRow>();

  ventas.forEach((v) => {
    if (v.estado === "anulada" || !ventaTienePropina(v)) return;
    const uid = v.usuarioId || "unknown";
    const name = v.userName?.trim() || "Sin nombre";
    const porMetodo =
      v.servicioPorMetodo ??
      buildServicioPorMetodo({
        total: v.total,
        servicioAmount: getVentaServicioAmount(v),
        metodoPago: v.metodoPago,
        breakdown: v.detalleMetodoPago?.breakdown,
      });

    const row = map.get(uid) ?? {
      usuarioId: uid,
      userName: name,
      porMetodo: { ...EMPTY },
      total: 0,
      ventasConPropina: 0,
    };
    METODOS_PROPINA.forEach((m) => {
      row.porMetodo[m] += porMetodo[m] ?? 0;
    });
    row.total = totalPropinaMontos(row.porMetodo);
    row.ventasConPropina += 1;
    map.set(uid, row);
  });

  return [...map.values()].sort((a, b) => b.total - a.total);
};

import { extractPaymentLinesFromVenta } from "./paymentMethodAggregation";

/** Suma totales de venta por método (incluye propina en el total cobrado). */
export const sumVentasPorMetodo = (ventas: Venta[]): MontosPorMetodoPago => {
  const out = { ...EMPTY };
  ventas.forEach((v) => {
    if (v.estado === "anulada") return;
    allocateVentaAmountBySlot(v, v.total).forEach((amt, slotId) => {
      const bucket = slotToLegacyBucket(slotId);
      if (bucket !== "otros") out[bucket] += amt;
    });
  });
  return out;
};

/**
 * Cobrado real por canal (líneas de pago POS). En mixto usa el breakdown
 * guardado (ya incluye propina tras enriquecer venta); evita duplicar propina.
 */
export const sumCobradoPorMetodo = (ventas: Venta[]): MontosPorMetodoPago => {
  const out = { ...EMPTY };
  ventas.forEach((v) => {
    if (v.estado === "anulada") return;
    const lines = extractPaymentLinesFromVenta(v);
    if (lines.length > 0) {
      for (const ln of lines) {
        const key = toPropinaKey(ln.slotId);
        if (key) out[key] += Math.round(ln.amount);
      }
      return;
    }
    const key = toPropinaKey(v.metodoPago);
    if (key) out[key] += Math.round(v.total);
  });
  return out;
};

/** Ventas netas (subtotal productos) por método — para cierre sin mezclar propina. */
export const sumSubtotalPorMetodo = (ventas: Venta[]): MontosPorMetodoPago => {
  const out = { ...EMPTY };
  ventas.forEach((v) => {
    if (v.estado === "anulada") return;
    allocateVentaAmountBySlot(v, getVentaSubtotal(v)).forEach((amt, slotId) => {
      const bucket = slotToLegacyBucket(slotId);
      if (bucket !== "otros") out[bucket] += amt;
    });
  });
  return out;
};

export const totalMontos = (montos: MontosPorMetodoPago): number =>
  montos.efectivo + montos.nequi + montos.daviplata + montos.tarjeta;

/** % de propina que retiene el negocio (0–100). Opcional en `businessConfig`. */
export function readPropinaPctNegocio(): number {
  try {
    const cfg = txRead<{ propinaPctNegocio?: unknown }>("businessConfig", {});
    const n = Number(cfg.propinaPctNegocio);
    if (!Number.isFinite(n) || n < 0 || n > 100) return 0;
    return Math.round(n);
  } catch {
    return 0;
  }
}

export function splitPropinaNegocioEmpleados(
  totalPropina: number,
  pctNegocio = readPropinaPctNegocio(),
): { negocio: number; empleados: number; pctNegocio: number } {
  const negocio = Math.round((totalPropina * pctNegocio) / 100);
  return {
    negocio,
    empleados: Math.max(0, totalPropina - negocio),
    pctNegocio,
  };
}

export interface VentasPorEmpleadoRow {
  userName: string;
  total: number;
  count: number;
}

/** Agrupa ventas cobradas por quien registró la venta. */
export const groupVentasPorEmpleado = (
  ventas: Venta[],
): VentasPorEmpleadoRow[] => {
  const map = new Map<string, VentasPorEmpleadoRow>();
  ventas.forEach((v) => {
    if (v.estado === "anulada") return;
    const uid = v.usuarioId || "unknown";
    const name = v.userName?.trim() || "Sin nombre";
    const row = map.get(uid) ?? { userName: name, total: 0, count: 0 };
    row.total += v.total || 0;
    row.count += 1;
    map.set(uid, row);
  });
  return [...map.values()].sort((a, b) => b.total - a.total);
};

export {
  sumNetoIngresoPorMetodo,
  sumTarifasUsoPorMetodo,
  totalTarifasUso,
  turnoTieneTarifasUso,
} from "./paymentMethodUsageFee";

/** Ventas activas de una sesión (excluye anuladas). */
export const ventasActivasSesion = (
  ventas: Venta[],
  sesionId?: string,
): Venta[] =>
  ventas.filter(
    (v) => v.estado !== "anulada" && (!sesionId || v.sesionId === sesionId),
  );
